import { useCallback, useEffect, useState } from 'react'
import { DEFAULT_FAVOURITE_PATHS } from './navItems'

/**
 * The pages a person pinned to the top of the sidebar, kept on this device.
 *
 * Stored in localStorage rather than the database on purpose: it costs no
 * request on load (see .claude/rules/costs.md), and the trade-off is that the
 * list does not follow someone to another phone. Keyed per person and per gym,
 * so two staff sharing a desk computer each keep their own.
 */
export function useNavFavourites(userKey: string | undefined, gymId: string) {
  const storageKey = `gym-nav-favourites:${gymId}:${userKey ?? 'anon'}`
  const [favourites, setFavourites] = useState<string[]>(() => read(storageKey))

  // A gym switch or a different person changes the key: load their list.
  useEffect(() => {
    setFavourites(read(storageKey))
  }, [storageKey])

  const update = useCallback(
    (next: string[]) => {
      setFavourites(next)
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // Private mode or storage blocked: the change still applies until reload.
      }
    },
    [storageKey],
  )

  const toggle = useCallback(
    (path: string) =>
      update(
        favourites.includes(path) ? favourites.filter((p) => p !== path) : [...favourites, path],
      ),
    [favourites, update],
  )

  const moveUp = useCallback(
    (path: string) => {
      const i = favourites.indexOf(path)
      if (i <= 0) return
      const next = [...favourites]
      next.splice(i - 1, 2, favourites[i], favourites[i - 1])
      update(next)
    },
    [favourites, update],
  )

  return { favourites, toggle, moveUp }
}

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.every((p) => typeof p === 'string')) return parsed
    }
  } catch {
    // Unreadable or blocked storage falls back to the defaults.
  }
  return [...DEFAULT_FAVOURITE_PATHS]
}
